import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Star, ShoppingCart, ArrowSquareOut, Lightning, Users,
  TwitterLogo, YoutubeLogo, InstagramLogo, TiktokLogo, Globe, Fire
} from '@phosphor-icons/react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function StarRating({ rating }) {
  const r = parseFloat(rating) || 0;
  return (
    <div className="flex items-center gap-1">
      <div className="flex">{[1,2,3,4,5].map(n => <Star key={n} size={13} weight="fill" className={n <= Math.round(r) ? 'text-amber-400' : 'text-zinc-300'} />)}</div>
      <span className="text-xs text-zinc-500 ml-0.5">{rating}</span>
    </div>
  );
}

function ProductCard({ product, rank }) {
  const imgSrc = product.product_image
    ? (product.product_image.startsWith('/api') ? `${API_BASE}${product.product_image}` : product.product_image)
    : null;

  return (
    <a
      href={product.affiliate_url}
      target="_blank"
      rel="noreferrer"
      className="group block bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-zinc-100 hover:border-amber-200"
      data-testid={`shop-product-${product.link_id}`}
    >
      {/* Rank */}
      {rank && (
        <div className="absolute top-0 left-3 z-10">
          <div className="bg-amber-500 text-white text-xs font-bold px-2.5 py-1 rounded-b-lg shadow-md">#{rank}</div>
        </div>
      )}

      {/* Image */}
      <div className="relative aspect-square bg-white p-4 flex items-center justify-center overflow-hidden">
        {imgSrc ? (
          <img src={imgSrc} alt={product.product_title} className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-300" onError={(e) => { e.target.style.display = 'none'; }} />
        ) : (
          <div className="text-zinc-200"><ShoppingCart size={48} /></div>
        )}
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="bg-amber-500 text-white rounded-full p-1.5 shadow-lg">
            <ArrowSquareOut size={14} weight="bold" />
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="p-3 space-y-1.5 bg-zinc-50/50">
        <h3 className="text-sm text-blue-700 group-hover:text-amber-600 leading-snug line-clamp-3 font-medium transition-colors">
          {product.product_title || product.asin}
        </h3>
        {product.product_rating && <StarRating rating={product.product_rating} />}
        {product.product_price && (
          <p className="text-xl font-bold text-red-600">{product.product_price}</p>
        )}
        <div className="flex items-center gap-2 pt-1">
          <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px] font-medium px-2 py-0.5">
            <ShoppingCart size={10} className="mr-0.5" /> Buy on Amazon
          </Badge>
          <Badge variant="outline" className="text-[9px] text-zinc-400 border-zinc-200">{product.region}</Badge>
        </div>
      </div>
    </a>
  );
}

export default function PublicShop() {
  const { creatorSlug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function load() {
      try {
        const url = creatorSlug
          ? `${API_BASE}/api/amazon/shop/${creatorSlug}`
          : `${API_BASE}/api/amazon/shop`;
        const res = await fetch(url);
        if (!res.ok) throw new Error('Shop not found');
        setData(await res.json());
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [creatorSlug]);

  if (loading) return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
      <div className="animate-pulse text-zinc-400">Loading shop...</div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-3xl font-bold text-zinc-800 mb-2">Shop Not Found</h1>
        <p className="text-zinc-500">{error}</p>
        <Link to="/" className="text-blue-600 text-sm mt-4 inline-block hover:underline">Go to Dynamic World</Link>
      </div>
    </div>
  );

  const products = data?.products || [];
  const creator = data?.creator;
  const socials = creator?.social_links || {};

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="public-shop">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">DW</div>
            <span className="font-bold text-zinc-800 text-lg hidden sm:block">Dynamic World</span>
            {creator && <span className="text-zinc-400 hidden sm:block">/</span>}
            {creator && <span className="text-zinc-600 font-medium text-sm">{creator.display_name}'s Shop</span>}
          </div>
          <div className="flex items-center gap-2">
            <Link to="/login" className="text-sm text-blue-600 hover:text-blue-700 font-medium">Log In</Link>
            <Link to="/register">
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-xs h-8 px-4">Join Free</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Creator Profile (if viewing a specific creator's shop) */}
      {creator && (
        <div className="bg-white border-b border-zinc-200">
          <div className="max-w-7xl mx-auto px-4 py-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-2xl font-bold text-white shrink-0">
                {creator.display_name?.[0] || 'S'}
              </div>
              <div>
                <h1 className="text-xl font-bold text-zinc-900">{creator.display_name}'s Picks</h1>
                {creator.bio && <p className="text-sm text-zinc-500 mt-0.5 max-w-lg">{creator.bio}</p>}
                <div className="flex items-center gap-3 mt-2 text-zinc-400">
                  {socials.twitter && <a href={`https://twitter.com/${socials.twitter.replace('@','')}`} target="_blank" rel="noreferrer" className="hover:text-blue-500 transition-colors"><TwitterLogo size={18} /></a>}
                  {socials.youtube && <a href={`https://youtube.com/@${socials.youtube}`} target="_blank" rel="noreferrer" className="hover:text-red-500 transition-colors"><YoutubeLogo size={18} /></a>}
                  {socials.instagram && <a href={`https://instagram.com/${socials.instagram.replace('@','')}`} target="_blank" rel="noreferrer" className="hover:text-pink-500 transition-colors"><InstagramLogo size={18} /></a>}
                  {socials.tiktok && <a href={`https://tiktok.com/@${socials.tiktok.replace('@','')}`} target="_blank" rel="noreferrer" className="hover:text-zinc-800 transition-colors"><TiktokLogo size={18} /></a>}
                  {socials.website && <a href={socials.website} target="_blank" rel="noreferrer" className="hover:text-zinc-800 transition-colors"><Globe size={18} /></a>}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Products */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {!creator && (
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-zinc-900 flex items-center gap-2"><Fire size={24} className="text-amber-500" /> Trending Products</h1>
            <p className="text-sm text-zinc-500 mt-1">Curated picks from our top creators. Click any product to buy on Amazon.</p>
          </div>
        )}

        {products.length === 0 ? (
          <div className="text-center py-16">
            <ShoppingCart size={48} className="mx-auto text-zinc-300 mb-3" />
            <p className="text-zinc-500 font-medium">No products yet</p>
            <p className="text-xs text-zinc-400 mt-1">Check back soon for amazing deals!</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {products.map((product, idx) => (
              <div key={product.link_id} className="relative">
                <ProductCard product={product} rank={idx + 1} />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Disclosure Footer */}
      <footer className="bg-white border-t border-zinc-200 py-6 mt-8">
        <div className="max-w-7xl mx-auto px-4 text-center space-y-2">
          <p className="text-[11px] text-zinc-400 italic max-w-2xl mx-auto">
            {creator?.disclosure_text || 'As an Amazon Associate, I earn from qualifying purchases. Prices shown are approximate and may vary.'}
          </p>
          <div className="flex items-center justify-center gap-4 text-xs text-zinc-400">
            <span>Powered by Dynamic World</span>
            <span>&middot;</span>
            <Link to="/" className="text-blue-500 hover:underline">Start Earning</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
